"use client";
import { useCallback, useEffect, useMemo, useState } from "react";

export interface UseSignedUrlResult {
    signedUrl: string;
    signedUrls: Record<string, string>;
    isLoading: boolean;
}

// 1. Global Client Memory Cache (fileId -> signedUrl)
const clientUrlCache = new Map<string, string>();

// 2. Global Batch Queue to collapse multiple concurrent hook calls into 1 API request
const pendingFileIds = new Set<string>();
const listeners = new Set<(urls: Record<string, string>) => void>();
let batchTimer: NodeJS.Timeout | null = null;

const flushBatch = async () => {
    if (pendingFileIds.size === 0) return;

    const idsToFetch = Array.from(pendingFileIds);
    pendingFileIds.clear();

    try {
        const res = await fetch("/api/upload/signed-urls", {
            method: "POST",
            body: JSON.stringify({ fileIds: idsToFetch }),
            headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();

        if (data.success && data.data) {
            const fetchedUrls: Record<string, string> = data.data;
            Object.entries(fetchedUrls).forEach(([id, url]) => {
                clientUrlCache.set(id, url);
            });

            // Notify all listening components
            listeners.forEach((listener) => listener(fetchedUrls));
        }
    } catch (error) {
        console.error("Batch signed URL fetch error:", error);
    }
};

const queueBatchRequest = (fileIds: string[]) => {
    let hasUncached = false;
    for (const id of fileIds) {
        if (!clientUrlCache.has(id)) {
            pendingFileIds.add(id);
            hasUncached = true;
        }
    }

    if (hasUncached) {
        if (batchTimer) clearTimeout(batchTimer);
        batchTimer = setTimeout(flushBatch, 15);
    }
};

export const useSignedUrl = (
    fileIds?: string | (string | undefined)[],
): UseSignedUrlResult => {
    const idsKey = useMemo(() => {
        if (!fileIds) return "";
        const list = Array.isArray(fileIds) ? fileIds : [fileIds];
        return list
            .filter((id): id is string => Boolean(id) && typeof id === "string")
            .join(",");
    }, [fileIds]);

    const idsArray = useMemo(() => (idsKey ? idsKey.split(",") : []), [idsKey]);

    // Check how many URLs are already cached
    const getCachedState = useCallback(() => {
        const cachedMap: Record<string, string> = {};
        let missing = false;

        for (const id of idsArray) {
            if (clientUrlCache.has(id)) {
                cachedMap[id] = clientUrlCache.get(id)!;
            } else {
                missing = true;
            }
        }
        return { cachedMap, missing };
    }, [idsArray]);

    const [signedUrls, setSignedUrls] = useState<Record<string, string>>(() => {
        return getCachedState().cachedMap;
    });

    const [isLoading, setIsLoading] = useState<boolean>(() => {
        if (!idsKey) return false;
        return getCachedState().missing;
    });

    useEffect(() => {
        if (!idsKey) {
            setIsLoading(false);
            setSignedUrls({});
            return;
        }

        const { cachedMap, missing } = getCachedState();
        setSignedUrls(cachedMap);

        if (!missing) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        const handleUpdate = () => {
            const updated = getCachedState();
            setSignedUrls(updated.cachedMap);
            if (!updated.missing) {
                setIsLoading(false);
            }
        };

        listeners.add(handleUpdate);
        queueBatchRequest(idsArray);

        return () => {
            listeners.delete(handleUpdate);
        };
    }, [idsKey, idsArray, getCachedState]);

    const firstId = idsArray[0] || "";
    const singleSignedUrl = firstId ? signedUrls[firstId] || "" : "";

    return {
        signedUrl: singleSignedUrl,
        signedUrls,
        isLoading,
    };
};
